import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Check } from "lucide-react";

const AUDIENCES = [
  ["resident", "Local resident / citizen"],
  ["customer", "Data center customer / end user"],
  ["operator", "Operator or hyperscaler"],
  ["developer", "Developer or landowner"],
  ["utility", "Utility / infrastructure professional"],
  ["public_official", "Public official"],
  ["journalist", "Journalist or researcher"],
  ["other", "Other"],
];

const TOPICS = ["Water", "Power", "Cooling", "Jobs", "Tax Revenue", "Emissions", "Noise", "Community", "Policy", "International"];

export default function NewsletterSignup({ compact = false }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("");
  const [audience, setAudience] = useState("resident");
  const [topics, setTopics] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = (t) => setTopics((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email, state: state || null, topics, audience_type: audience,
      });
      setDone(true);
      if (data.email_configured) toast.success("Check your inbox to confirm your subscription.");
      else toast.success("You're signed up! (Email confirmation will send once delivery is configured.)");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="newsletter-success" className="card p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-[#84CC16]/20 flex items-center justify-center mx-auto mb-4">
          <Check className="text-[#84CC16]" />
        </div>
        <h3 className="font-display font-bold text-xl text-white">You're on the list</h3>
        <p className="text-slate-400 mt-2 text-sm">We'll send a confirmation email. You can update your preferences or unsubscribe anytime — we never sell or rent your data.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-form" className="card p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-1"><Mail size={16} className="text-[#84CC16]" /><span className="eyebrow">Free weekly newsletter</span></div>
      <h3 className="font-display font-bold text-2xl text-white mb-4">Get the facts in your inbox</h3>
      <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 mb-3" />

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <select data-testid="newsletter-audience-select" value={audience} onChange={(e) => setAudience(e.target.value)}
          className="bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-blue-500">
          {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="newsletter-state-input" value={state} onChange={(e) => setState(e.target.value.toUpperCase())}
          placeholder="State or region (optional)" maxLength={20}
          className="bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
      </div>

      {!compact && (
        <div className="mb-4">
          <p className="text-xs text-slate-400 mb-2 font-mono uppercase tracking-wider">Topics you care about</p>
          <div className="flex flex-wrap gap-2">
            {TOPICS.map((t) => (
              <button type="button" key={t} data-testid={`topic-${t}`} onClick={() => toggle(t)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${topics.includes(t)
                  ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/15 text-slate-300 hover:border-blue-500/50"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <button data-testid="newsletter-submit-btn" type="submit" disabled={loading}
        className="w-full bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17] font-bold py-3 rounded-lg transition-colors disabled:opacity-60 glow-lime">
        {loading ? "Signing up…" : "Sign up free"}
      </button>
      <p className="text-[11px] text-slate-500 mt-3 text-center">No spam. One-click unsubscribe. We never sell or rent your list. See our <a href="/privacy" className="underline">privacy policy</a>.</p>
    </form>
  );
}
